import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Spin } from "antd"
import { useDispatch, useSelector } from "react-redux"
import PageHeader from "../../components/Common/PageHeader"
import EmptyState from "../../components/Common/EmptyState"
import Table from "../../components/Common/Table"
import OrganizationStats from "../../components/Organization/OrganizationStats"
import AddOrganizationMemberModal from "../../components/Organization/AddOrganizationMemberModal"
import AllocateOrganizationMemberModal from "../../components/Organization/AllocateOrganizationMemberModal"
import ReclaimOrganizationMemberModal from "../../components/Organization/ReclaimOrganizationMemberModal"
import {
  fetchOrganizationMembers,
  fetchOrganizationOverview,
  setPoolUnallocated,
  setPoolUnallocatedFurnitureFinder,
} from "../../ReduxToolKit/Slice/OrganizationSlice"
import {
  getOrganizationMemberColumns,
  mapMemberForTable,
} from "../../utils/TableData/Organization/organizationMemberColumns"
import { organizationMemberActions } from "../../utils/TableData/Organization/organizationMemberActions"
import { translations } from "../../utils/Translation/staticTranslation"
import { getAppLanguage } from "../../utils/getAppLanguage"
import {
  getAllowedPlanFeaturesFromOverview,
  hasOrganizationFurnitureFinderPool,
} from "../../utils/organizationApiHelpers"
import "../../styles/Pages/organization.css"

const OrganizationTeam = () => {
  const dispatch = useDispatch()
  const { overview, members, overviewLoading, membersLoading } = useSelector(
    (state) => state.organization,
  )
  const lang = getAppLanguage()
  const t = translations[lang]?.organizationPage

  const [addOpen, setAddOpen] = useState(false)
  const [allocateMember, setAllocateMember] = useState(null)
  const [reclaimMember, setReclaimMember] = useState(null)

  const loadData = useCallback(() => {
    dispatch(fetchOrganizationOverview())
    dispatch(fetchOrganizationMembers())
  }, [dispatch])

  useEffect(() => {
    loadData()
  }, [loadData])

  const allowedPlanFeatures = useMemo(
    () => getAllowedPlanFeaturesFromOverview(overview),
    [overview],
  )
  const unallocated = overview?.subscription?.unallocated_images ?? 0
  const unallocatedFurnitureFinder =
    overview?.subscription?.unallocated_furniture_finder ?? 0
  const showFurnitureFinder = hasOrganizationFurnitureFinderPool(overview)

  const memberColumns = useMemo(
    () => getOrganizationMemberColumns(lang, showFurnitureFinder),
    [lang, showFurnitureFinder],
  )

  const refreshOverview = useCallback(() => {
    dispatch(fetchOrganizationOverview())
  }, [dispatch])

  const openAddMember = () => {
    refreshOverview()
    setAddOpen(true)
  }

  const openAllocate = (row) => {
    refreshOverview()
    setAllocateMember(row)
  }

  const tableData = useMemo(
    () => (members || []).map((m) => mapMemberForTable(m, lang)),
    [members, lang],
  )

  const handleAllocateSuccess = (result) => {
    if (result?.pool_unallocated_images != null) {
      dispatch(setPoolUnallocated(result.pool_unallocated_images))
    }
    if (result?.pool_unallocated_furniture_finder != null) {
      dispatch(
        setPoolUnallocatedFurnitureFinder(result.pool_unallocated_furniture_finder),
      )
    }
    loadData()
  }

  const handleReclaimSuccess = (result) => {
    if (result?.pool_unallocated_images != null) {
      dispatch(setPoolUnallocated(result.pool_unallocated_images))
    }
    if (result?.pool_unallocated_furniture_finder != null) {
      dispatch(
        setPoolUnallocatedFurnitureFinder(result.pool_unallocated_furniture_finder),
      )
    }
    loadData()
  }

  const handleAddSuccess = () => {
    loadData()
  }

  const loading = overviewLoading && !overview

  return (
    <div className="organization-team-page">
      <PageHeader
        title={t?.pageTitle}
        buttonText={t?.addMember}
        forceShowButton
        onClick={openAddMember}
      />

      {loading ? (
        <div className="d-flex justify-content-center align-items-center org-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {overview ? <OrganizationStats overview={overview} /> : null}

          <div className="org-members-section">
            <div className="org-members-panel">
              <h2 className="org-section-title">{t?.teamTitle}</h2>
              {membersLoading && tableData.length === 0 ? (
                <div className="d-flex justify-content-center py-48">
                  <Spin />
                </div>
              ) : tableData.length > 0 ? (
                <Table
                  columns={memberColumns}
                  data={tableData}
                  actionsColumnWidth="200px"
                  actionsAlign="center"
                  actions={organizationMemberActions(
                    (row) => openAllocate(row),
                    (row) => setReclaimMember(row),
                  )}
                />
              ) : (
                <div className="org-empty-wrap">
                  <EmptyState
                    title={t?.emptyTitle}
                    description={t?.emptyDescription}
                  />
                  <button
                    type="button"
                    className="add-store org-empty-cta"
                    onClick={openAddMember}
                  >
                    <img src="/icons/addicon.svg" alt="" className="mr-6" />
                    {t?.addMember}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <AddOrganizationMemberModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={handleAddSuccess}
        allowedPlanFeatures={allowedPlanFeatures}
        maxImages={unallocated}
        maxFurnitureFinder={unallocatedFurnitureFinder}
        showFurnitureFinder={showFurnitureFinder}
      />

      <AllocateOrganizationMemberModal
        open={Boolean(allocateMember)}
        onClose={() => setAllocateMember(null)}
        onSuccess={handleAllocateSuccess}
        member={allocateMember}
        allowedPlanFeatures={allowedPlanFeatures}
        maxImages={unallocated}
        maxFurnitureFinder={unallocatedFurnitureFinder}
        showFurnitureFinder={showFurnitureFinder}
      />

      <ReclaimOrganizationMemberModal
        open={Boolean(reclaimMember)}
        onClose={() => setReclaimMember(null)}
        onSuccess={handleReclaimSuccess}
        member={reclaimMember}
        showFurnitureFinder={showFurnitureFinder}
      />
    </div>
  )
}

export default OrganizationTeam
